import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Card,
  Button,
  Row,
  Col,
  notification,
  Tag,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { formatDateInTimezone } from '../lib/timezones';
import { setPageMeta } from '../lib/seo';
import { useCurrency } from '../lib/useCurrency';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function OrderPage() {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const { formatFromEur, formatAmount } = useCurrency();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchOrder = async () => {
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const res = await axios.get(`${API_URL}/api/orders/${orderNumber}`, config);
        setOrder(res.data);
      } catch (err) {
        notification.error({ message: 'Failed to load order' });
        navigate('/admin');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderNumber, navigate]);

  useEffect(() => {
    setPageMeta({
      title: `Order ${orderNumber || ''} ? CLOUNIQ`,
      description: 'Order details for your CLOUNIQ resources.',
    });
  }, [orderNumber]);

  const displayTimezone = useMemo(
    () => order?.User?.timezone || order?.start_timezone || 'UTC',
    [order],
  );

  const durationHours = useMemo(() => {
    if (!order?.start_time || !order?.end_time) return 0;
    const start = new Date(order.start_time);
    const end = new Date(order.end_time);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) return 0;
    return (end - start) / (1000 * 60 * 60);
  }, [order]);

  const itemsCostPerHour = useMemo(() => {
    return Array.isArray(order?.items)
      ? order.items.reduce((sum, item) => sum + (item?.is_free ? 0 : Number(item?.price_delta || 0)), 0)
      : 0;
  }, [order]);

  const basePrice = useMemo(
    () => Number(order?.Resource?.base_price_per_hour || 0),
    [order],
  );

  if (loading || !order) {
    return (
      <div className="admin-order-page workspace-page admin-panel">
        <div className="workspace-backdrop"></div>
        <div className="container">
          <div className="glass userpage-card">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-order-page workspace-page admin-panel">
      <div className="workspace-backdrop"></div>
      <div className="container">
        <div className="admin-user-header">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} className="resource-btn-ghost">
            Back
          </Button>
        </div>

        <div className="glass userpage-card admin-order-hero">
          <div className="userpage-title">Order Details</div>
          <div className="admin-order-hero-grid">
            <div>
              <div className="admin-order-number">{order.order_number}</div>
              <div className="admin-order-meta">
                <span>User: {order?.User?.email || '-'}</span>
                <span>Resource: {order?.Resource?.name || '-'}</span>
              </div>
            </div>
            <div className="admin-order-hero-tags">
              <Tag color={order.status === 'active' ? 'green' : order.status === 'completed' ? 'blue' : order.status === 'cancelled' ? 'red' : 'orange'}>
                {order.status}
              </Tag>
              <Tag color={(order.payment_status === 'pending' ? 'payment_awaited' : order.payment_status) === 'paid' ? 'green' : (order.payment_status === 'pending' ? 'payment_awaited' : order.payment_status) === 'payment_failed' ? 'red' : 'orange'}>
                {(order.payment_status === 'pending' ? 'payment_awaited' : order.payment_status) === 'paid'
                  ? 'Paid'
                  : (order.payment_status === 'pending' ? 'payment_awaited' : order.payment_status) === 'payment_failed'
                  ? 'Payment failed'
                  : 'Payment awaited'}
              </Tag>
            </div>
          </div>

          <div className="admin-order-summary">
            <div className="admin-order-summary-item">
              <span className="admin-order-summary-label">Timezone</span>
              <span className="admin-order-summary-value">{displayTimezone}</span>
            </div>
            <div className="admin-order-summary-item">
              <span className="admin-order-summary-label">Start</span>
              <span className="admin-order-summary-value">{formatDateInTimezone(order.start_time, displayTimezone)}</span>
            </div>
            <div className="admin-order-summary-item">
              <span className="admin-order-summary-label">End</span>
              <span className="admin-order-summary-value">{formatDateInTimezone(order.end_time, displayTimezone)}</span>
            </div>
            <div className="admin-order-summary-item">
              <span className="admin-order-summary-label">Duration</span>
              <span className="admin-order-summary-value">{durationHours.toFixed(2)}h</span>
            </div>
          </div>
        </div>

        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <Card className="glass admin-order-card" title="Order Information">
              <div className="admin-order-info">
                <div className="admin-order-info-row">
                  <span className="admin-order-info-label">Order Number</span>
                  <span className="admin-order-info-value">{order.order_number}</span>
                </div>
                <div className="admin-order-info-row">
                  <span className="admin-order-info-label">User</span>
                  <span className="admin-order-info-value">{order?.User?.email || '-'}</span>
                </div>
                <div className="admin-order-info-row">
                  <span className="admin-order-info-label">Resource</span>
                  <span className="admin-order-info-value">{order?.Resource?.name || '-'}</span>
                </div>
                <div className="admin-order-info-row">
                  <span className="admin-order-info-label">Resource Type</span>
                  <span className="admin-order-info-value">{order?.Resource?.type || '-'}</span>
                </div>
                <div className="admin-order-info-row">
                  <span className="admin-order-info-label">Payment ID</span>
                  <span className="admin-order-info-value">{order.payment_id || '-'}</span>
                </div>
                <div className="admin-order-info-row">
                  <span className="admin-order-info-label">Paynet Order ID</span>
                  <span className="admin-order-info-value">{order?.payment?.payNetOrderId || '-'}</span>
                </div>
                <div className="admin-order-info-row">
                  <span className="admin-order-info-label">Payment Status</span>
                  <span className="admin-order-info-value">
                    {(order.payment_status === 'pending' ? 'payment_awaited' : order.payment_status) === 'paid'
                      ? 'Paid'
                      : (order.payment_status === 'pending' ? 'payment_awaited' : order.payment_status) === 'payment_failed'
                      ? 'Payment failed'
                      : 'Payment awaited'}
                  </span>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card className="glass admin-order-card" title="Pricing">
              <div className="admin-order-info">
                <div className="admin-order-info-row">
                  <span className="admin-order-info-label">Base price</span>
                  <span className="admin-order-info-value">{formatFromEur(basePrice)}/hr</span>
                </div>
                <div className="admin-order-info-row">
                  <span className="admin-order-info-label">Components</span>
                  <span className="admin-order-info-value">{formatFromEur(itemsCostPerHour)}/hr</span>
                </div>
                <div className="admin-order-info-row">
                  <span className="admin-order-info-label">Hourly rate</span>
                  <span className="admin-order-info-value">{formatFromEur(basePrice + itemsCostPerHour)}/hr</span>
                </div>
                <div className="admin-order-info-row">
                  <span className="admin-order-info-label">Total</span>
                  <span className="admin-order-info-value admin-order-total">{order?.currency || order?.order_currency ? formatAmount(Number(order.total_cost || 0), order.currency || order.order_currency) : formatFromEur(Number(order.total_cost || 0))}</span>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        <div className="glass userpage-card">
          <div className="userpage-title">Components</div>
          <div className="admin-order-components">
            {(order.items || []).length === 0 && (
              <div className="admin-order-empty">No components selected.</div>
            )}
            {(order.items || []).map((item, idx) => (
              <div key={`${item.name}-${idx}`} className="admin-order-component-row">
                <div>
                  <div className="admin-order-component-name">{item.name}</div>
                  <div className="admin-order-component-value">{item.value}</div>
                </div>
                <div className={`admin-order-component-price ${item.is_free ? 'is-free' : ''}`}>
                  {item.is_free ? 'Free' : `+${formatFromEur(Number(item.price_delta || 0))}/hr`}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderPage;



